import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { NewsletterSignup } from "@/components/newsletter-signup";
import { postSummaries as posts } from "@/data/post-index";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Digital Marketing & SEO Strategy Blog | MG Content Labs" },
      {
        name: "description",
        content:
          "Guides on SEO content strategy, answer engine optimization, B2B digital marketing, and editorial craft — written by the MG Content Labs team in Delhi.",
      },
      { property: "og:title", content: "Digital Marketing & SEO Blog | MG Content Labs" },
      {
        property: "og:description",
        content:
          "Essays and playbooks on semantic SEO, answer engine optimization, digital marketing strategy, and editorial craft for modern B2B brands.",
      },
      { property: "og:url", content: "https://mgcontentlabs.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/blog" }],
  }),
  component: BlogList,
});

function BlogList() {
  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const [active, setActive] = useState("All");
  const visible = active === "All" ? posts : posts.filter((p) => p.category === active);

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />
      <header className="bg-brand-muted py-24">
        <div className="mx-auto max-w-7xl px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">The Journal</span>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl md:text-6xl">
            Digital marketing and <span className="italic text-brand-accent">SEO content strategy.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-brand-deep/70">
            Practical guides on SEO content strategy, answer engine optimization, B2B digital
            marketing, and the editorial systems that turn content into compounding revenue.
          </p>
        </div>
      </header>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 flex flex-wrap gap-3">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                aria-pressed={active === c}
                className={
                  active === c
                    ? "rounded-full bg-brand-accent px-4 py-2 text-xs font-bold uppercase tracking-widest text-white"
                    : "rounded-full border border-brand-deep/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-deep/70 transition-colors hover:border-brand-accent hover:text-brand-accent"
                }
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((p) => (

              <Link to="/blog/$slug" params={{ slug: p.slug }} key={p.slug} className="group block">
                <img
                  src={p.cover}
                  alt={p.title}
                  width={1200}
                  height={800}
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover outline outline-1 -outline-offset-1 outline-black/5"
                />
                <time className="mt-6 block text-xs font-bold uppercase tracking-widest text-brand-accent">
                  {p.category} · {new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · {p.readTime}
                </time>
                <h2 className="mt-2 text-xl font-bold transition-colors group-hover:text-brand-accent">
                  {p.title}
                </h2>
                <p className="mt-3 text-sm text-brand-deep/60">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6">
          <NewsletterSignup source="blog-index" />
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
